import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, Button, Snackbar, TextField, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import URLManager from "../funcionalidadeNavbar/URLManager";
import CronoanaliseTabela from './CronoanaliseTabela';
import CronoanaliseEtapas from './CronoanaliseEtapas';
import { CronometroService } from './services/cronometroService';
import ConfirmationModal from '../components/ConfirmationModal';

const Cronoanalise = () => {
  const _url = URLManager.getBaseURL();
  const usuarioLogado = sessionStorage.getItem("usuarioNome") || "";

  // Estado inicial
  const initialCronoanalise = {
    data: new Date(),
    operador: '',
    modelo: '',
    setor: '',
    operacao: '',
    cronometrista: usuarioLogado,
    maqPosto: '',
    freqLogicaNumerador: '',
    freqLogicaDenominador: '',
    eficiencia: '',
    tolerancia: '',
    idEmpresa: 0,

    tempo: 0,
    tempoCalculado: 0,

    temposDetalhados: [],

    produtividadeHoraInf: '',
    produtividadeHora: 0,
    produtividadeDiaInf: '',
    produtividadeDia: 0,
  };

  // Estados principais
  const [tela, setTela] = useState('selecaoProduto'); // 'selecaoProduto', 'formulario', 'historico'
  const [etapaAtual, setEtapaAtual] = useState(1);
  const [objCronoanalise, setObjCronoanalise] = useState(initialCronoanalise);
  const [cronoanalises, setCronoanalises] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [popperMessage, setPopperMessage] = useState('');

  // Estados do modal de confirmação
  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  // Estados de produtos
  const [produtosPai, setProdutosPai] = useState([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [carregandoProdutos, setCarregandoProdutos] = useState(false);
  const [filtrarProdutos, setFiltrarProdutos] = useState('');

  // Estados do vídeo e cronômetro
  const [videoFile, setVideoFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [preparaTimes, setPreparaTimes] = useState([]);
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [recordingMode, setRecordingMode] = useState('video');
  const [loading, setLoading] = useState(false);


  const [processoId, setProcessoId] = useState(null);

  // Estados para edição
  const [cronoEmEdicao, setCronoEmEdicao] = useState(null);
  const [modalEdicaoAberto, setModalEdicaoAberto] = useState(false);
  const [dadosEdicao, setDadosEdicao] = useState({
    setor: '',
    operacao: '',
    operador: '',
    maqPosto: '',
    freqLogicaNumerador: '',
    freqLogicaDenominador: '',
    eficiencia: '',
    tolerancia: '',
    produtividadeHoraInf: 0,
    produtividadeDiaInf: 0
  });

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  // Removemos o timer local; o tempo agora vem apenas do backend para evitar saltos.

  // Carrega produtos pai ao montar o componente
  useEffect(() => {
    if (!carregarProdutosPai) return;
    carregarProdutosPai();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Função para carregar produtos pai (com corPiloto != null)
  const carregarProdutosPai = async () => {
    setCarregandoProdutos(true);
    try {
      const url = `${_url}/produto/so-pais`;
      console.log('[Cronoanalise] Carregando produtos de:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem("usuarioToken")}`,
          'Accept': 'application/json'
        }
      });

      console.log('[Cronoanalise] Status da resposta:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Cronoanalise] Erro na resposta:', errorText);
        throw new Error(`Erro: ${response.status} - ${errorText}`);
      }
      
      let data = await response.json();
      console.log('[Cronoanalise] Dados recebidos:', data);
      
      // O endpoint /produto/so-pais já retorna apenas produtos pai
      if (Array.isArray(data)) {
        // Normaliza idProduto
        const normalized = data.map(p => ({
          ...p,
          id: p.idProduto || p.id
        }));
        console.log('[Cronoanalise] Produtos normalizados:', normalized.length, normalized);
        setProdutosPai(normalized);
      } else {
        console.warn('[Cronoanalise] Dados não são um array:', data);
        setProdutosPai([]);
      }
    } catch (error) {
      console.error('[Cronoanalise] Erro ao carregar produtos pai:', error);
      setPopperMessage('Erro ao carregar produtos: ' + error.message);
      setSnackbarOpen(true);
      setProdutosPai([]);
    } finally {
      setCarregandoProdutos(false);
    }
  };

  // Handler para selecionar um produto e iniciar cronoanálise
  const iniciarCronoanaliseComProduto = (produto) => {
    setProdutoSelecionado(produto);
    setObjCronoanalise(prev => ({
      ...prev,
      idProduto: produto.id,
      modelo: produto.nomeModelo || produto.nome,
      operacao: '',
      data: new Date()
    }));
    setTela('formulario');
    setEtapaAtual(1);
  };

  // Handler para voltar à seleção de produtos
  const voltarParaSelecao = () => {
    setTela('selecaoProduto');
    setProdutoSelecionado(null);
    setEtapaAtual(1);
    setPreparaTimes([]);
    setElapsedTime(0);
    setIsRecording(false);
    setVideoFile(null);
  };

  // Handler para confirmar cancelamento
  const handleConfirmarCancelamento = () => {
    setModalConfirm({ ...modalConfirm, open: false });
    voltarParaSelecao();
  };

  // Handler para abrir modal de cancelamento
  const handleCancelarComConfirmacao = () => {
    setModalConfirm({
      open: true,
      title: 'Cancelar Cronoanálise',
      message: 'Deseja realmente cancelar todo o processo?',
      description: 'Todos os dados não salvos serão perdidos.',
      type: 'warning',
      onConfirm: handleConfirmarCancelamento,
      confirmText: 'Sim, Cancelar',
      cancelText: 'Continuar'
    });
  };

  // Handler para visualizar histórico do produto selecionado
  const verHistoricoSelecionado = async () => {
    if (!produtoSelecionado) return;
    await carregarCronoanalises(produtoSelecionado.id);
    setTela('historico');
  };

  // Função para carregar cronoanálises
  const carregarCronoanalises = useCallback(async (idProduto = null) => {
    const mostrarMensagemLocal = (mensagem) => {
      setPopperMessage(mensagem);
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 3000);
    };
  
    try {
      const endpoint = idProduto ? `${_url}/cronoanalise/produto/${idProduto}` : `${_url}/cronoanalise/listar`;
      console.debug('[Cronoanalise] GET endpoint:', endpoint, { idProduto });
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem("usuarioToken")}`,
          'Accept': 'application/json'
        }
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro HTTP: ${response.status} - ${errorText}`);
      }
  
      const responseText = await response.text();
      
      // Verifica se a resposta está vazia ou truncada
      if (!responseText || !responseText.trim() || !responseText.startsWith('[')) {
        console.warn('Resposta do servidor vazia ou inválida:', responseText);
        setCronoanalises([]);
        return;
      }
  
      try {
        const data = JSON.parse(responseText);
        if (!Array.isArray(data)) {
          throw new Error('Resposta não é um array');
        }
        
        // Filtra possíveis valores nulos
        const filteredData = data.filter(item => item != null);
              
        setCronoanalises(filteredData);
        
      } catch (parseError) {
        console.error("Erro ao fazer parse do JSON:", parseError);
        console.error("Conteúdo recebido:", responseText.substring(0, 200));
        throw new Error('Resposta do servidor não é um JSON válido');
      }
    } catch (error) {
      console.error("Erro ao carregar cronoanálises:", error);
      mostrarMensagemLocal(error.message || 'Erro ao carregar cronoanálises');
      setCronoanalises([]);
    }
  }, [_url]);

  // Verifica disponibilidade da câmera
  useEffect(() => {
    const checkCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setCameraAvailable(true);
        }
      } catch (error) {
        console.error("Câmera não disponível:", error);
        setCameraAvailable(false);
        setRecordingMode('timer');
      }
    };

    checkCamera();
  }, []);

  // Inicia gravação
  const startRecording = async (mode) => {
    try {
      setRecordingMode(mode);

      // Gerar um ID único para o processo (timestamp em milissegundos - long)
    const novoProcessoId = Date.now();
    setProcessoId(novoProcessoId);
    
    // Iniciar cronômetro no backend
    await CronometroService.iniciar(novoProcessoId);

      if (mode === 'video' && cameraAvailable) {
        const constraints = {
          video: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: true
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        videoRef.current.srcObject = stream;
        mediaRecorderRef.current = new MediaRecorder(stream);
        const chunks = [];

        mediaRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/mp4' });
          setVideoFile(new File([blob], 'cronoanalise.mp4', { type: 'video/mp4' }));
        };

        mediaRecorderRef.current.start();
      }

      setElapsedTime(0);
      setIsRecording(true);
      setPreparaTimes([]);

    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      if (mode === 'video') {
        setRecordingMode('timer');
        startRecording('timer');
      }
    }
  };

  // Para gravação
const stopRecording = async () => {  // Adicionei async aqui
  try {
    if (processoId) {
      // Obter o tempo final do backend (em segundos)
      const tempoFinal = await CronometroService.parar(processoId);
      // Backend retorna tempo em segundos, converter para milissegundos corretamente
      setElapsedTime(Math.floor(tempoFinal * 1000));
    }
    setIsRecording(false);
    
    if (recordingMode === 'video' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    }
  } catch (error) {
    console.error("Erro ao parar cronômetro:", error);
  }
}; 

     // Nova função para atualizar o tempo periodicamente
useEffect(() => {
  if (isRecording && processoId) {
    const interval = setInterval(async () => {
      try {
        const tempoAtual = await CronometroService.getTempo(processoId);
        // Backend retorna tempo em segundos, converter para milissegundos corretamente
        setElapsedTime(Math.floor(tempoAtual * 1000));
      } catch (error) {
        console.error("Erro ao atualizar tempo:", error);
      }
    }, 100); // Atualiza a cada 100ms

    return () => clearInterval(interval);
  }
}, [isRecording, processoId]);


  // Registra tempo
  const registrarPrepara = () => {
    const novoTempo = {
      id: Date.now(),
      time: elapsedTime,
      selected: true
    };
    setPreparaTimes(prev => [...prev, novoTempo]);
  };

  // Formata tempo
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const milliseconds = ms % 1000;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`;
  };

  // Mostra mensagem
  const mostrarMensagem = (mensagem) => {
    setPopperMessage(mensagem);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 3000);
  };

  // Atualiza campo do formulário
  const digitar = (e) => {
    const { name, value } = e.target;
    setObjCronoanalise(prev => ({
      ...prev,
      [name]: name === 'data' ? new Date(value) : value
    }));
  };

  // Limpa campos
  const limparCampos = () => {
    setObjCronoanalise(prev => ({
      ...initialCronoanalise,
      data: prev.data,
      modelo: prev.modelo,
      setor: prev.setor,
      cronometrista: prev.cronometrista
    }));
    setVideoFile(null);
    setElapsedTime(0);
    setPreparaTimes([]);
    setRecordingMode('video');
  };

  // Cadastra cronoanálise
  const cadastraClonoanalise = async () => {
    setLoading(true);
   
      try {
    // Validar tempos detalhados
    if (preparaTimes.length === 0) {
      mostrarMensagem('Adicione pelo menos um tempo detalhado antes de salvar');
      setLoading(false);
      return;
    }

    // Converter tempos para segundos - IMPORTANTE: backend trabalha em SEGUNDOS
    const temposDetalhados = preparaTimes.map(item => ({
      time: Number(item.time / 1000), // em SEGUNDOS (tipo numérico)
      selected: item.selected === true
    }));
  
      const cronoData = {
        data: objCronoanalise.data.toISOString().split('T')[0],
        operador: objCronoanalise.operador || '',
        modelo: objCronoanalise.modelo || '',
        setor: objCronoanalise.setor || '',
        operacao: objCronoanalise.operacao || '',
        cronometrista: objCronoanalise.cronometrista || usuarioLogado,
        maqPosto: objCronoanalise.maqPosto || '',
        freqLogicaNumerador: Number(objCronoanalise.freqLogicaNumerador) || 1,
        freqLogicaDenominador: Number(objCronoanalise.freqLogicaDenominador) || 1,
        eficiencia: Number(objCronoanalise.eficiencia) || 100,
        tolerancia: Number(objCronoanalise.tolerancia) || 0,
        produtividadeHoraInf: Number(objCronoanalise.produtividadeHoraInf) || 0,
        produtividadeDiaInf: Number(objCronoanalise.produtividadeDiaInf) || 0,
        idProduto: objCronoanalise.idProduto || null,
        temposDetalhados: temposDetalhados
      };
  
      const formData = new FormData();
      formData.append('dados', JSON.stringify(cronoData));
      
      if (videoFile) {
        formData.append('video', videoFile);
      }
  
      // Decide endpoint: se houver produto selecionado, tentar POST /cronoanalise/produto/{idProduto}/cadastrar
      const targetProdutoId = objCronoanalise.idProduto || null;
      const productEndpoint = targetProdutoId ? `${_url}/cronoanalise/produto/${targetProdutoId}/cadastrar` : null;
      const generalEndpoint = `${_url}/cronoanalise/cadastrarCronoanalise`;

      // Helper para enviar e retornar response
      const sendTo = async (url) => {
        console.debug('[Cronoanalise] Enviando para endpoint:', url, { targetProdutoId });
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem("usuarioToken")}`
          },
          body: formData
        });
        const text = await resp.text();
        return { resp, text };
      };

      let finalResponseText = '';
      // Se houver targetProdutoId, tente o endpoint product-scoped e, se 404, caia para o endpoint geral
      if (productEndpoint) {
        const { resp, text } = await sendTo(productEndpoint);
        finalResponseText = text;
        if (resp.status === 404) {
          console.warn('[Cronoanalise] Endpoint product-scoped não encontrado (404). Tentando endpoint geral.');
          // garantir que o backend possa identificar o produto via payload
          cronoData.idProduto = targetProdutoId;
          formData.set('dados', JSON.stringify(cronoData));
          const retry = await sendTo(generalEndpoint);
          finalResponseText = retry.text;
          if (!retry.resp.ok) {
            throw new Error(finalResponseText || `Erro ao salvar cronoanálise (retry ${retry.resp.status})`);
          }
        } else if (!resp.ok) {
          throw new Error(text || `Erro ao salvar cronoanálise (${resp.status})`);
        }
      } else {
        const { resp, text } = await sendTo(generalEndpoint);
        finalResponseText = text;
        if (!resp.ok) {
          throw new Error(text || `Erro ao salvar cronoanálise (${resp.status})`);
        }
      }
  
      mostrarMensagem('Operação cadastrada com sucesso!');
      limparCampos();
      await carregarCronoanalises();
      setEtapaAtual(2);
  
    } catch (error) {
      console.error("[Cronoanalise] Erro ao enviar:", error);
      console.error("[Cronoanalise] Stack:", error.stack);
      mostrarMensagem(error.message || 'Falha ao salvar cronoanálise');
    } finally {
      setLoading(false);
    }
  };

  // Função para remover cronoanálise
  const removerCronoanalise = async (id) => {
    try {
      const response = await fetch(`${_url}/cronoanalise/remover/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem("usuarioToken")}`,
          'Content-Type': 'application/json'
        }
      });

      const responseText = await response.text();
      let responseData = null;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        // Se não conseguir fazer parse, continua sem dados
      }

      // Verifica se foi bem-sucedido (status 200) ou se contém mensagem de sucesso
      const foiSucesso = response.ok || 
                        (responseData && responseData.mensagem && responseData.mensagem.includes('removido com sucesso'));

      if (!foiSucesso) {
        const errorMsg = responseData?.mensagem || responseText || 'Erro ao remover cronoanálise';
        throw new Error(errorMsg);
      }

      // Remover da lista local
      setCronoanalises(prevList => {
        const novaLista = prevList.filter(item => item.id !== id);
        console.log('Cronoanálise removida da lista. Total restante:', novaLista.length);
        return novaLista;
      });
      
      mostrarMensagem('Cronoanálise removida com sucesso');
      
    } catch (error) {
      console.error('Erro ao remover cronoanálise:', error);
      mostrarMensagem(error.message || 'Falha ao remover cronoanálise');
    }
  };

  // Função para abrir modal de edição
  const abrirModalEdicao = (cronoanalise) => {
    setCronoEmEdicao(cronoanalise);
    setDadosEdicao({
      setor: cronoanalise.setor || '',
      operacao: cronoanalise.operacao || '',
      operador: cronoanalise.operador || '',
      maqPosto: cronoanalise.maqPosto || '',
      freqLogicaNumerador: cronoanalise.freqLogicaNumerador || '',
      freqLogicaDenominador: cronoanalise.freqLogicaDenominador || '',
      eficiencia: cronoanalise.eficiencia || '',
      tolerancia: cronoanalise.tolerancia || '',
      produtividadeHoraInf: cronoanalise.produtividadeHoraInf || 0,
      produtividadeDiaInf: cronoanalise.produtividadeDiaInf || 0
    });
    setModalEdicaoAberto(true);
  };

  // Função para alterar cronoanálise
  const alterarCronoanalise = async () => {
    try {
      if (!cronoEmEdicao) return;

      const response = await fetch(`${_url}/cronoanalise/alterar/${cronoEmEdicao.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem("usuarioToken")}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dadosEdicao)
      });

      const responseText = await response.text();
      let responseData = null;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        // Se não conseguir fazer parse, continua sem dados
      }

      if (!response.ok) {
        const errorMsg = responseData?.mensagem || responseText || 'Erro ao alterar cronoanálise';
        throw new Error(errorMsg);
      }

      mostrarMensagem('Cronoanálise alterada com sucesso');
      setModalEdicaoAberto(false);
      
      // Recarregar a lista
      if (produtoSelecionado) {
        await carregarCronoanalises(produtoSelecionado.id);
      } else {
        await carregarCronoanalises();
      }
    } catch (error) {
      console.error('Erro ao alterar cronoanálise:', error);
      mostrarMensagem(error.message || 'Falha ao alterar cronoanálise');
    }
  };

  // Fechar modal de edição
  const fecharModalEdicao = () => {
    setModalEdicaoAberto(false);
    setCronoEmEdicao(null);
    setDadosEdicao({
      setor: '',
      operacao: '',
      operador: '',
      maqPosto: '',
      freqLogicaNumerador: '',
      freqLogicaDenominador: '',
      eficiencia: '',
      tolerancia: '',
      produtividadeHoraInf: 0,
      produtividadeDiaInf: 0
    });
  };

  // Confirmar remoção
  const confirmarRemocao = (id, modelo) => {
    setModalConfirm({
      open: true,
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja remover a cronoanálise do produto "${modelo}"? Esta ação não pode ser desfeita.`,
      type: 'error',
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setModalConfirm(prev => ({ ...prev, open: false }));
        await removerCronoanalise(id);
      }
    });
  };

  // Handlers de navegação
  const handleIniciarCronometragem = () => {
    if (!objCronoanalise.modelo || !objCronoanalise.setor) {
      mostrarMensagem('Preencha todos os campos obrigatórios');
      return;
    }
    setEtapaAtual(2);
  };

  const handleNovaCronometragem = () => {
    cadastraClonoanalise();
  };

  const handleFinalizarCronometragem = () => {
    setEtapaAtual(1);
    limparCampos();
    voltarParaSelecao();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        {/* TELA 1: SELEÇÃO DE PRODUTOS */}
        {tela === 'selecaoProduto' && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
              Iniciar Cronoanálise
            </Typography>

            {carregandoProdutos ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography>Carregando produtos...</Typography>
              </Box>
            ) : produtosPai.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography color="error">Nenhum produto disponível para cronoanálise</Typography>
              </Box>
            ) : (
              <Box>
                <TextField
                  placeholder="Filtrar produtos..."
                  value={filtrarProdutos}
                  onChange={(e) => setFiltrarProdutos(e.target.value)}
                  fullWidth
                  sx={{ mb: 3 }}
                />
                
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 2
                }}>
                  {produtosPai
                    .filter(p => {
                      const termo = filtrarProdutos.toLowerCase();
                      return (
                        (p.nome && p.nome.toLowerCase().includes(termo)) ||
                        (p.nomeModelo && p.nomeModelo.toLowerCase().includes(termo)) ||
                        (p.descricao && p.descricao.toLowerCase().includes(termo)) ||
                        (p.marca && p.marca.toLowerCase().includes(termo)) ||
                        (p.referencia && p.referencia.toLowerCase().includes(termo))
                      );
                    })
                    .map(produto => {
                      // Processar imagem do produto
                      const raw = produto && produto.image ? String(produto.image) : '';
                      const imageSrc = raw
                        ? (raw.startsWith('http') || raw.startsWith('data:')
                          ? raw
                          : `data:image/jpeg;base64,${raw}`)
                        : '';
                      
                      return (
                      <Paper
                        key={produto.id}
                        sx={{
                          p: 3,
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-4px)'
                          }
                        }}
                        onClick={() => iniciarCronoanaliseComProduto(produto)}
                      >
                        {/* Imagem do Produto */}
                        {imageSrc ? (
                          <Box
                            component="img"
                            src={imageSrc}
                            alt={produto.nomeModelo || produto.nome}
                            sx={{
                              width: '100%',
                              height: 200,
                              objectFit: 'contain',
                              borderRadius: 1,
                              backgroundColor: '#f5f5f5',
                              mb: 2
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              backgroundColor: '#f5f5f5',
                              border: '1px dashed #ccc',
                              mb: 2,
                              color: '#999'
                            }}
                          >
                            <Typography variant="caption">Sem imagem</Typography>
                          </Box>
                        )}
                        
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {produto.nomeModelo || produto.nome}
                        </Typography>
                        {produto.descricao && (
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {produto.descricao}
                          </Typography>
                        )}
                        <Box sx={{
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                          mb: 2
                        }}>
                          {produto.corPiloto && (
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: '#e3f2fd',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 'bold'
                              }}
                            >
                              Cor: {produto.corPiloto}
                            </Typography>
                          )}
                          {produto.marca && (
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: '#f3e5f5',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 'bold'
                              }}
                            >
                              {produto.marca}
                            </Typography>
                          )}
                          {produto.referencia && (
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: '#e8f5e9',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 'bold'
                              }}
                            >
                              Ref: {produto.referencia}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          Iniciar Cronoanálise
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProdutoSelecionado(produto);
                            verHistoricoSelecionado();
                          }}
                        >
                          Ver Processos
                        </Button>
                      </Paper>
                      );
                    })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* TELA 2: FORMULÁRIO DE CRONOANÁLISE */}
        {tela === 'formulario' && produtoSelecionado && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Cronoanálise: {produtoSelecionado.nome}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Etapa {etapaAtual} de 4
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancelarComConfirmacao}
              >
                Cancelar
              </Button>
            </Box>

            <CronoanaliseEtapas
              etapa={etapaAtual}
                objCronoanalise={objCronoanalise}
                eventoTeclado={digitar}
                onIniciarCronometragem={handleIniciarCronometragem}
                onNovaCronometragem={handleNovaCronometragem}
                onFinalizarCronometragem={handleFinalizarCronometragem}
                isRecording={isRecording}
                elapsedTime={elapsedTime}
                formatTime={formatTime}
                videoRef={videoRef}
                recordingMode={recordingMode}
                startRecording={startRecording}
                stopRecording={stopRecording}
                registrarPrepara={registrarPrepara}
                preparaTimes={preparaTimes}
                setPreparaTimes={setPreparaTimes}
                loading={loading}
                cameraAvailable={cameraAvailable}
                processoId={processoId}
                CronometroService={CronometroService}
                setEtapaAtual={setEtapaAtual}
                setObjCronoanalise={setObjCronoanalise}
                cadastraClonoanalise={cadastraClonoanalise}
              />
          </Box>
        )}

        {/* TELA 3: HISTÓRICO DE CRONOANÁLISES */}
        {tela === 'historico' && produtoSelecionado && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Histórico: {produtoSelecionado.nome}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => iniciarCronoanaliseComProduto(produtoSelecionado)}
                >
                  Nova Cronoanálise
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setTela('selecaoProduto')}
                >
                  Voltar
                </Button>
              </Box>
            </Box>

            <CronoanaliseTabela
              vetor={cronoanalises}
              selecionar={abrirModalEdicao}
              remover={confirmarRemocao}
            />
          </Box>
        )}

        {/* SNACKBAR GLOBAL */}
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={popperMessage}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={modalConfirm.open}
          title={modalConfirm.title}
          message={modalConfirm.message}
          description={modalConfirm.description}
          type={modalConfirm.type}
          confirmText={modalConfirm.confirmText}
          cancelText={modalConfirm.cancelText}
          onConfirm={() => {
            if (modalConfirm.onConfirm) {
              modalConfirm.onConfirm();
            }
          }}
          onCancel={() => setModalConfirm({ ...modalConfirm, open: false })}
        />

        {/* Modal de Edição */}
        <Dialog open={modalEdicaoAberto} onClose={fecharModalEdicao} maxWidth="sm" fullWidth>
          <DialogTitle>Editar Cronoanálise</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Setor"
                value={dadosEdicao.setor}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, setor: e.target.value })}
                fullWidth
              />
              <TextField
                label="Operação"
                value={dadosEdicao.operacao}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, operacao: e.target.value })}
                fullWidth
              />
              <TextField
                label="Operador"
                value={dadosEdicao.operador}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, operador: e.target.value })}
                fullWidth
              />
              <TextField
                label="Máquina/Posto"
                value={dadosEdicao.maqPosto}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, maqPosto: e.target.value })}
                fullWidth
              />
              <TextField
                label="Frequência Lógica - Numerador"
                type="number"
                value={dadosEdicao.freqLogicaNumerador}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, freqLogicaNumerador: e.target.value })}
                fullWidth
              />
              <TextField
                label="Frequência Lógica - Denominador"
                type="number"
                value={dadosEdicao.freqLogicaDenominador}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, freqLogicaDenominador: e.target.value })}
                fullWidth
              />
              <TextField
                label="Eficiência (%)"
                type="number"
                value={dadosEdicao.eficiencia}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, eficiencia: e.target.value })}
                fullWidth
              />
              <TextField
                label="Tolerância (%)"
                type="number"
                value={dadosEdicao.tolerancia}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, tolerancia: e.target.value })}
                fullWidth
              />
              <TextField
                label="Produtividade Hora (Inf)"
                type="number"
                value={dadosEdicao.produtividadeHoraInf}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, produtividadeHoraInf: e.target.value })}
                fullWidth
              />
              <TextField
                label="Produtividade Dia (Inf)"
                type="number"
                value={dadosEdicao.produtividadeDiaInf}
                onChange={(e) => setDadosEdicao({ ...dadosEdicao, produtividadeDiaInf: e.target.value })}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharModalEdicao}>Cancelar</Button>
            <Button onClick={alterarCronoanalise} variant="contained" color="primary">
              Salvar Alterações
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Cronoanalise;